import { Operation } from '@apollo/client'
import { ErrorResponse } from '@apollo/client/link/error'
import { getMainDefinition } from '@apollo/client/utilities'
import { isPlainObject } from 'lodash'
import { NextPageContext } from 'next'

import { isBrowser } from '~lib/helpers'

/**
 * Checks presence of authorization error
 */
export const hasAuthorizationError = (error: ErrorResponse): boolean => {
  return error.graphQLErrors?.some(({ message }) => message === 'Unauthorized')
}

/**
 * Checks presence of not found error
 */
// TODO
export const hasNotFoundError = (error: ErrorResponse): boolean => {
  return error.graphQLErrors?.some(({ message }) => message.endsWith(' not found'))
}

/**
 * Checks presence of email verification error
 */
export const hasVerificationError = (error: ErrorResponse): boolean => {
  // return error.graphQLErrors?.some(({ message }) => message === AUTH_ERROR_TYPES.EMAIL_NOT_VERIFIED)
  return error.graphQLErrors?.some(({ message }) => message === 'Email is not verified')
}

/**
 * Logs error link errors
 */
export const logLinkErrors = (error: ErrorResponse) => {
  if (error.graphQLErrors) {
    error.graphQLErrors.forEach(({ message, locations, path }) =>
      console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
    )
  }

  if (error.networkError) {
    console.error(`[Network error]: ${error.networkError}`)
  }
}

/**
 * Checks whether to use websocket link
 */
export const testUseWebSocketLink = (operation: Operation) => {
  const definition = getMainDefinition(operation.query)
  return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
}

/**
 * Checks whether to use public launch page apollo link
 * All public launch page queries must be prefixed with PublicLaunchPage
 * Alternatively launch page queries can be determined by unique context value,
 * but it will be required more efforts to support, because we will need to write that value to each query manually
 */
export const testUsePublicLaunchApolloLink = (operation: Operation) => {
  return (
    operation.operationName.startsWith('PublicLaunchPage') || operation.operationName.startsWith('PublicLaunchTask')
  )
}

/**
 * Checks whether to use public company page apollo link
 * All public company page queries must be prefixed with PublicCompanyPage
 * Alternatively company page queries can be determined by unique context value,
 * but it will be required more efforts to support, because we will need to write that value to each query manually
 */
export const testUsePublicCompanyApolloLink = (operation: Operation) => {
  return operation.operationName.startsWith('PublicCompany')
}

export function omitTypename<T extends { __typename?: string } = { __typename?: string }>(obj: T) {
  const { __typename, ...rest } = obj
  return Object.fromEntries(
    [...Object.entries(rest)].map(([key, value]) => {
      if (Array.isArray(value)) {
        return [key, value.map((v) => (isPlainObject(v) ? omitTypename(v) : v))]
      }
      return [key, isPlainObject(value) ? omitTypename(value) : value]
    })
  )
}

export function getIsomorphicXOrganizationSlugHeader(ctx: NextPageContext | undefined): string | null {
  if (!isBrowser()) {
    if (!ctx?.query) {
      // Logger.getInstance().trackError(new Error(`[getIsomorphicOrganizationSlug] Cannot get parsed query from ${ctx}`))
      return null
    }

    return ctx.query.organizationSlug as string
  }

  const [first, second] = window.location.pathname.split('/').slice(1)

  if (
    (first === 'public' && second === 'growth-tool') || // TODO move growth-tool from /public
    [
      'auth',
      'ui',
      '404',
      '500',
      'email-spam',
      'integration-callback',
      'not-ready',
      'oauth-popup',
      'thank-you',
    ].includes(first)
  ) {
    // TODO move common pages to path
    return null
  }
  // /public/[organizationSlug]/* or [organizationSlug]/*
  return first === 'public' ? second : first
}

export function isPublicPagePathname(pathname: string): boolean {
  const [first, second] = pathname.split('/').slice(1)

  return first === 'public' && second !== 'growth-tool'
}
